import { useCallback, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { carriesLabMeasurements, type ClientOrder } from "@ferrivia/api-contract";
import { BOOKING_URL } from "@ferrivia/booking-copy";
import { useTracker } from "@ferrivia/auth-web";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Sheet, SheetContent, SheetTitle, Text } from "@ferrivia/ui";
import { useAppContext } from "../../app/useAppContext";
import { useDismissOnBack } from "../../hooks/useDismissOnBack";
import { CopyButton } from "../CopyButton";
import { useMyOrders } from "../../hooks/useMyOrders";
import s from "./OrdersView.module.css";

function hasLabPanel(order: ClientOrder): boolean {
  return order.items.some((item) => carriesLabMeasurements(item.fulfillmentKind));
}

/* What the row says the order was. The lines' own snapshotted names, joined —
   a receipt names what was sold, not what today's catalog calls it (see
   `clientOrderItemSchema`). A quantity is only worth saying when it isn't one. */
function summarise(order: ClientOrder): string {
  return order.items.map((item) => (item.quantity > 1 ? `${item.quantity}× ${item.productName}` : item.productName)).join(" + ");
}

/** "Tilaukseni", opened from ProfileMenu. */
export function OrdersSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { rootElement } = useAppContext();

  useDismissOnBack(open, () => onOpenChange(false), "orders");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent container={rootElement} aria-describedby={undefined}>
        <OrdersView />
      </SheetContent>
    </Sheet>
  );
}

/**
 * A client's own purchases: the list, and one order's detail.
 *
 * The two states are a `useState` here and not two URLs — the client app's
 * history is deliberately never deeper than home plus a tab (see
 * app/TabNavigation.tsx). The sheet's own drag and back gesture close the
 * list; the back control only appears on the detail.
 */
export function OrdersView() {
  const { t } = useTranslation("client");
  const { date, currencyEur } = useFormatters();
  const tracker = useTracker();
  const { state } = useMyOrders(true);
  const [openNumber, setOpenNumber] = useState<string | null>(null);

  const open = state.status === "success" ? (state.orders.find((o) => o.orderNumber === openNumber) ?? null) : null;

  const handleOpen = useCallback(
    (order: ClientOrder) => {
      tracker.track("store.order_detail_viewed", { has_lab_panel: hasLabPanel(order) });
      setOpenNumber(order.orderNumber);
    },
    [tracker],
  );

  return (
    <>
      <div className={s.header}>
        {openNumber && (
          <button
            type="button"
            className={s.back}
            onClick={() => setOpenNumber(null)}
            aria-label={t("store.orders.back", "Takaisin")}
          >
            <ChevronLeft aria-hidden="true" />
          </button>
        )}
        <SheetTitle className={s.title}>
          {open ? t("store.orders.detailTitle", "Tilaus") : t("store.orders.title", "Tilaukset")}
        </SheetTitle>
      </div>

      <div className={s.body}>
        {state.status === "loading" && (
          <Text as="p" size="sm" className={s.notice}>
            {t("store.orders.loading", "Haetaan tilauksiasi…")}
          </Text>
        )}

        {state.status === "error" && (
          <Text as="p" size="sm" className={s.notice}>
            {t("store.orders.error", "Tilauksia ei juuri nyt saatu haettua. Yritä hetken kuluttua uudestaan.")}
          </Text>
        )}

        {state.status === "success" && state.orders.length === 0 && (
          <Text as="p" size="sm" className={s.notice}>
            {t("store.orders.empty", "Sinulla ei ole vielä tilauksia. Tekemäsi tilaukset näkyvät täällä.")}
          </Text>
        )}

        {state.status === "success" &&
          state.orders.length > 0 &&
          (open ? (
            <OrderDetail order={open} customerNumber={state.customerNumber} />
          ) : (
            state.orders.map((order) => (
              <button key={order.orderNumber} type="button" className={s.row} onClick={() => handleOpen(order)}>
                <span className={s.rowMain}>
                  <span className={s.number}>{order.orderNumber}</span>
                  <span className={s.what}>{summarise(order)}</span>
                  <span className={s.when}>{date.format(new Date(order.paidAt ?? order.placedAt))}</span>
                </span>
                <span className={s.rowRight}>
                  <span className={s.amount}>{currencyEur.format(order.totalCents / 100)}</span>
                  {order.refundedTotalCents > 0 && (
                    <span className={s.chip}>{t("store.orders.refunded", "Hyvitetty")}</span>
                  )}
                </span>
              </button>
            ))
          ))}
      </div>
    </>
  );
}

function OrderDetail({ order, customerNumber }: { order: ClientOrder; customerNumber: string | null }) {
  const { t } = useTranslation("client");
  const { date, currencyEur } = useFormatters();
  const tracker = useTracker();

  return (
    <>
      <div className={s.idCard}>
        <span className={s.idLabel}>{t("store.orders.showAtLab", "Näytä tämä laboratoriossa")}</span>
        <div className={s.idRow}>
          <span className={s.idFieldLabel}>{t("store.orders.orderLabel", "Tilausnumero")}</span>
          <span className={s.idNumber}>{order.orderNumber}</span>
        </div>
        {customerNumber !== null && (
          <div className={s.idRow}>
            <span className={s.idFieldLabel}>{t("store.orders.customerLabel", "Asiakasnumero")}</span>
            <span className={s.idNumber}>{customerNumber}</span>
          </div>
        )}
        <CopyButton
          text={customerNumber !== null ? `${order.orderNumber}, ${customerNumber}` : order.orderNumber}
          label={t("store.orders.copy", "Kopioi")}
          copiedLabel={t("store.orders.copied", "Kopioitu")}
          onCopy={() => tracker.track("store.order_number_copied", {})}
          className={s.copy}
        />
      </div>

      <div className={s.facts}>
        {/* Payment side only. Whether a paid lab panel has actually been drawn
            is not knowable for a webstore order until the lab integration
            lands, and a chip that guessed would be worse than no chip. */}
        <span className={s.factLabel}>{t("store.orders.paidLabel", "Tila")}</span>
        <span className={s.factValue}>
          {t("store.orders.paidOn", "Maksettu {{date}}", {
            date: date.format(new Date(order.paidAt ?? order.placedAt)),
          })}
          {order.refundedTotalCents > 0 && (
            <>
              <br />
              <span className={s.factNote}>
                {t("store.orders.refundedAmount", "Hyvitetty {{amount}}", {
                  amount: currencyEur.format(order.refundedTotalCents / 100),
                })}
              </span>
            </>
          )}
        </span>

        <span className={s.factLabel}>{t("store.orders.contents", "Tilaukseen kuuluu")}</span>
        <span className={s.factValue}>
          {order.items.map((item, index) => (
            <span key={`${item.productName}-${index}`} className={s.lineRow}>
              <span>{item.quantity > 1 ? `${item.quantity}× ${item.productName}` : item.productName}</span>
              <span>{currencyEur.format(item.lineTotalCents / 100)}</span>
            </span>
          ))}
        </span>

        {hasLabPanel(order) && (
          <>
            <span className={s.factLabel}>{t("store.orders.sampling", "Näytteenotto")}</span>
            <span className={s.factValue}>
              {t("store.orders.samplingPlace", "Arvoterveys, kaikki toimipisteet")}
              <br />
              <a href={BOOKING_URL} target="_blank" rel="noreferrer">
                {t("store.orders.book", "Varaa aika")}
              </a>
            </span>
          </>
        )}

        <span className={s.factLabel}>{t("store.orders.payment", "Maksu")}</span>
        <span className={s.factValue}>{currencyEur.format(order.totalCents / 100)}</span>
      </div>
    </>
  );
}
